import React, { useState, useEffect } from 'react';
// The /pure entry does not inject Stripe.js on import; it loads only when
// loadStripe() runs, i.e. when a card payment form actually opens.
import { loadStripe } from '@stripe/stripe-js/pure';
import { Elements, CardElement, useStripe, useElements, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useSiteSettings } from '../context/SiteSettingsContext';
import './PaymentStep.css';

let stripePromise = null;

const StripeCardForm = ({ clientSecret, onSuccess, onError, loading, setLoading }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setLoading(true);

    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: { card: elements.getElement(CardElement) }
    });

    if (result.error) {
      onError(result.error.message);
      setLoading(false);
    } else if (result.paymentIntent.status === 'succeeded') {
      onSuccess(result.paymentIntent.id);
    }
  };

  return (
    <div className="stripe-form">
      <CardElement
        options={{
          style: {
            base: {
              fontSize: '16px',
              color: '#374151',
              '::placeholder': { color: '#9ca3af' },
            },
          },
        }}
      />
      <button
        type="button"
        className="payment-submit-btn"
        onClick={handleSubmit}
        disabled={!stripe || loading}
      >
        {loading ? t('checkout.processing', 'Processing...') : t('payment.payNow', 'Pay Now')}
      </button>
    </div>
  );
};

const ExpressCheckout = ({ orderId, orderTotal, currency, onPaymentComplete, setError, setLoading }) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);

  useEffect(() => {
    if (!stripe || !orderTotal || !currency) return;

    const pr = stripe.paymentRequest({
      country: 'AL',
      currency: String(currency).toLowerCase(),
      total: {
        label: 'Order Total',
        amount: Math.round(orderTotal * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.canMakePayment().then(result => {
      if (result) {
        setCanMakePayment(true);
        setPaymentRequest(pr);
      }
    });

    pr.on('paymentmethod', async (ev) => {
      setLoading(true);
      try {
        const intentRes = await api.post('/api/payment/stripe/create-intent', { orderId });
        const clientSecret = intentRes.data.clientSecret;

        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (confirmError) {
          ev.complete('fail');
          setError(confirmError.message);
          setLoading(false);
          return;
        }

        ev.complete('success');

        if (paymentIntent.status === 'requires_action') {
          const { error: actionError } = await stripe.confirmCardPayment(clientSecret);
          if (actionError) {
            setError(actionError.message);
            setLoading(false);
            return;
          }
        }

        await api.post('/api/payment/stripe/confirm', {
          paymentIntentId: paymentIntent.id,
          orderId,
        });
        onPaymentComplete('stripe');
      } catch (err) {
        ev.complete('fail');
        setError(err.response?.data?.error || 'Express payment failed');
        setLoading(false);
      }
    });
  }, [stripe, orderTotal, currency, orderId, onPaymentComplete, setError, setLoading]);

  if (!canMakePayment || !paymentRequest) return null;

  return (
    <div className="express-checkout">
      <PaymentRequestButtonElement options={{ paymentRequest }} />
    </div>
  );
};

/**
 * The card or PayPal payment form, after the shopper chose that method on
 * the checkout form (the only methods that need a form of their own; pay
 * on delivery, bank transfer and IutePay finish on the checkout form). The
 * order is placed when the payment succeeds; "Back" returns to the form
 * and releases the checkout.
 */
const PaymentStep = ({ method, orderId, orderTotal, onPaymentComplete, onBack, paymentConfig }) => {
  const { t } = useTranslation();
  const { currency: ctxCurrency } = useSiteSettings();
  const currency = ctxCurrency || t('common.currency') || 'USD';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [paypalQuote, setPaypalQuote] = useState(null);
  const [paypalQuoteError, setPaypalQuoteError] = useState(null);

  if (method === 'stripe' && paymentConfig?.stripePublicKey && !stripePromise) {
    stripePromise = loadStripe(paymentConfig.stripePublicKey);
  }

  // Fetch the server-authoritative PayPal quote (canonical -> charge currency
  // conversion) so the disclosure cannot be tampered with from the client.
  useEffect(() => {
    if (method !== 'paypal' || !orderId || paypalQuote) return;
    api.get('/api/payment/paypal/quote', { params: { orderId } })
      .then(res => setPaypalQuote(res.data))
      .catch(err => setPaypalQuoteError(err.response?.data?.error || 'Failed to load PayPal quote'));
  }, [method, orderId, paypalQuote]);

  useEffect(() => {
    if (method === 'stripe' && orderId && !stripeClientSecret) {
      api.post('/api/payment/stripe/create-intent', { orderId })
        .then(res => setStripeClientSecret(res.data.clientSecret))
        .catch(err => setError(err.response?.data?.error || 'Failed to initialize Stripe'));
    }
  }, [method, orderId, stripeClientSecret]);

  const handleStripeSuccess = async (paymentIntentId) => {
    try {
      await api.post('/api/payment/stripe/confirm', { paymentIntentId, orderId });
      onPaymentComplete('stripe');
    } catch {
      setError('Payment confirmation failed');
      setLoading(false);
    }
  };

  const handlePayPalApprove = async (data) => {
    setLoading(true);
    try {
      await api.post('/api/payment/paypal/capture', { paypalOrderId: data.orderID, orderId });
      onPaymentComplete('paypal');
    } catch {
      setError('PayPal capture failed');
      setLoading(false);
    }
  };

  return (
    <div className="payment-step">
      <h2>{method === 'paypal' ? 'PayPal' : t('payment.creditCard', 'Credit / Debit Card')}</h2>

      {error && <div className="payment-error">{error}</div>}

      {method === 'stripe' && stripePromise && (
        <>
          <Elements stripe={stripePromise}>
            <ExpressCheckout
              orderId={orderId}
              orderTotal={orderTotal}
              currency={currency}
              onPaymentComplete={onPaymentComplete}
              setError={setError}
              setLoading={setLoading}
            />
          </Elements>
          <div className="express-divider">
            <span>{t('payment.orDivider', '— OR —')}</span>
          </div>
        </>
      )}

      <div className="payment-body">
        {method === 'stripe' && stripePromise && stripeClientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret: stripeClientSecret }}>
            <StripeCardForm
              clientSecret={stripeClientSecret}
              onSuccess={handleStripeSuccess}
              onError={(msg) => setError(msg)}
              loading={loading}
              setLoading={setLoading}
            />
          </Elements>
        )}

        {method === 'stripe' && !stripeClientSecret && !error && (
          <div className="payment-loading">{t('payment.initializing', 'Initializing payment...')}</div>
        )}

        {method === 'paypal' && paymentConfig?.paypalClientId && (
          <>
            {paypalQuoteError && (
              <div className="payment-error">{paypalQuoteError}</div>
            )}
            {paypalQuote && (
              <div className="paypal-fx-disclosure" style={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: 8,
                padding: '12px 14px',
                marginBottom: 12,
                fontSize: '0.875rem',
                color: '#1e40af',
                lineHeight: 1.5,
              }}>
                {paypalQuote.canonicalCurrency === paypalQuote.chargeCurrency ? (
                  <span>
                    {t('payment.paypalSameCurrency', 'PayPal will charge {{amount}} {{currency}}.', {
                      amount: parseFloat(paypalQuote.chargeAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                      currency: paypalQuote.chargeCurrency,
                    })}
                  </span>
                ) : (
                  <span>
                    {t(
                      'payment.paypalFxDisclosure',
                      'PayPal will charge {{chargeAmount}} {{chargeCurrency}} (≈ {{canonicalAmount}} {{canonicalCurrency}} at 1 {{chargeCurrency}} ≈ {{inverseRate}} {{canonicalCurrency}}). The exact amount in your bank statement may differ slightly due to PayPal’s own conversion fees.',
                      {
                        chargeAmount: parseFloat(paypalQuote.chargeAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                        chargeCurrency: paypalQuote.chargeCurrency,
                        canonicalAmount: parseFloat(paypalQuote.canonicalAmount).toLocaleString('en-US', { maximumFractionDigits: 0 }),
                        canonicalCurrency: paypalQuote.canonicalCurrency,
                        inverseRate: parseFloat(paypalQuote.inverseRate).toLocaleString('en-US', { maximumFractionDigits: 2 }),
                      }
                    )}
                  </span>
                )}
              </div>
            )}
            <PayPalScriptProvider options={{ 'client-id': paymentConfig.paypalClientId, currency: paypalQuote?.chargeCurrency || 'EUR' }}>
              <PayPalButtons
                style={{ layout: 'vertical', color: 'blue', shape: 'rect' }}
                createOrder={async () => {
                  const res = await api.post('/api/payment/paypal/create-order', { orderId });
                  return res.data.orderId;
                }}
                onApprove={handlePayPalApprove}
                onError={(err) => setError(err.message || 'PayPal error')}
                disabled={loading || !paypalQuote}
              />
            </PayPalScriptProvider>
          </>
        )}
      </div>

      {onBack && (
        <button type="button" className="payment-back-btn" onClick={onBack}>
          {t('common.back', 'Back')}
        </button>
      )}
    </div>
  );
};

export default PaymentStep;
